<template>
  <GridContainer
    class="product-offer-tile-xl-container"
    :class="{ 'product-offer-tile-xl-container--video-btn-right': videoButtonPosition === 'right' }"
  >
    <slot name="top-highlight" />
    <ProductOfferTileExtraLarge
      :ambient-image="ambientImage?.src"
      :video="video?.src"
      :offer-alternatives="offerAlternatives"
      :title="title"
      :description="description"
      :style="offerImagePosY"
      :animation-image="animationImage"
      :alignment="alignment"
      :heading-tag="headingTag"
      link-tag="nuxt-link"
      no-color-title
      :show-available-variant="showAvailableVariant"
    >
      <template #brand>
        <CdnImage
          v-if="brand?.logo && showBrandLogo"
          :src="brand.logo"
          :alt="`${brand.name} Logo`"
          :image-style="`aspect-ratio: ${brand.aspectRatio}`"
          class="product-offer-tile-xl-container__logo"
          lazy
          render-without-figure
        />
      </template>

      <template #heading>
        <slot name="heading" />
      </template>

      <template
        v-if="buttonDisabled"
        #variant
      >
        <span class="product-offer-tile-xl-container--alert">Bald wieder verfügbar </span>
      </template>

      <template #productImage="{ image }">
        <CdnImage
          v-if="showLocalImage && productImage.src"
          :src="productImage.src"
          :alt="image.name"
          :render-without-figure="productImageSizes === ''"
          :lazy="lazy"
          :sizes="productImageSizes"
        />
        <GoliathImageElement
          v-else
          :alt="image.name"
          :image="image"
          class="product-offer-tile-xl-container__image"
          xs-width="small"
          sm-width="medium"
          :lazy="lazy"
          :sizes="productImageSizes"
        />
      </template>

      <template #ambientImage>
        <CdnImage
          :src="ambientImage?.src"
          :alt="ambientImage?.alt"
          :do-not-convert="ambientImage?.doNotConvert || false"
          :render-without-figure="ambientImageSizes === ''"
          :lazy="lazy"
          :sizes="ambientImageSizes"
        />
      </template>
      <template #video="{ poster, videoSrc }">
        <VideoPlayer
          :poster="poster"
          :src="videoSrc"
          preload="metadata"
          autoplay
          loop
          muted
          :play-button-centered="false"
          :video-aria-label="videoAriaLabel"
        />
      </template>
      <template #button="{ activeVariant }">
        <AddToCartButtonContainer
          ref="cartButton"
          :offer="buttonDisabled ? {} : activeVariant?.offer"
          display-text
          :product-name="activeVariant?.offer?.product?.name"
        >
          <span class="product-offer-tile-xl-container__add-to-cart-button-label">
            In den Warenkorb
          </span>
        </AddToCartButtonContainer>
      </template>
      <template #energy="{ energyLabel, activeVariant }">
        <EnergyLabelContainer
          v-if="energyLabel"
          :product="activeVariant?.offer?.product"
          datasheet-alignment="justify"
        />
      </template>
    </ProductOfferTileExtraLarge>
  </GridContainer>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import {
  ProductOfferTileExtraLarge,
  GridContainer,
  VideoPlayer,
  GoliathImageElement,
} from '@i22-td-smarthome/component-library';

import CdnImage from '@/components/cdn-image.vue';
import { getFirstOfferAlternatives } from '@i22/rocket';
import EnergyLabelContainer from '@/components/energy-label-container.vue';
import type { ListOffer } from '~/lib/goliath/offers';
import { getBrand } from '~/lib/brand';
import AddToCartButtonContainer from '~/components/product-detail/add-to-cart-button-container.vue';

export type AmbientImage = {
  src: string;
  alt?: string;
  doNotConvert?: boolean;
};

export type ProductImage = {
  src?: string; // set, if you want a custom image!
  posY?: {
    xs?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
  };
};

const props = defineProps({
  offer: {
    type: Object as PropType<ListOffer>,
    default: () => {},
  },
  animationImage: {
    type: Object,
    default: () => ({
      start: 'top bottom',
      end: 'top-=15% center',
      vars: { y: 120 },
    }),
  },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  ambientImage: {
    type: Object as PropType<AmbientImage>,
    default: () => {},
  },
  productImage: {
    type: Object as PropType<ProductImage>,
    default: () => {},
  },
  video: {
    type: Object,
    default: () => {},
  },
  showLocalImage: { type: Boolean, default: false },
  showLocalTitle: { type: Boolean, default: false },
  alignment: { type: String, default: 'left' },
  headingTag: { type: String, default: 'h2' },
  buttonDisabled: { type: Boolean, default: false },
  showAvailableVariant: { type: Boolean },
  lazy: { type: Boolean },
  productImageSizes: { type: String, default: '' },
  ambientImageSizes: { type: String, default: '' },
  videoAriaLabel: { type: String, default: '' },
  videoButtonPosition: { type: String, default: 'left' },
  showBrandLogo: { type: Boolean },
});

const offerImagePosY = computed(() => {
  const items = [];
  const el = props.productImage?.posY;
  if (el?.xs) items.push(`--hero-pos-xs: ${el.xs};`);
  if (el?.sm) items.push(`--hero-pos-sm: ${el.sm};`);
  if (el?.md) items.push(`--hero-pos-md: ${el.md};`);
  if (el?.lg) items.push(`--hero-pos-lg: ${el.lg};`);
  if (el?.xl) items.push(`--hero-pos-xl: ${el.xl};`);
  return items.join('');
});
const offerAlternatives = computed(() =>
  getFirstOfferAlternatives(props.offer?.productAlternatives)
);

const brand = computed(() => getBrand(props.offer));
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.product-offer-tile-xl-container {
  &--alert {
    color: var(--color-primary);
  }
  &__add-to-cart-button-label {
    display: none;
    @include mq($mq-sm) {
      display: inline;
    }
  }
  :deep(.product-offer-tile-xl__content-link) {
    display: inline-block;
  }

  :deep(.product-offer-tile-xl__product-image) {
    --fallback-xs-pos: var(--hero-pos-xs, -10%);
    bottom: var(--fallback-xs-pos);
    @include mq($mq-sm) {
      bottom: var(--hero-pos-sm, var(--fallback-xs-pos));
    }
    @include mq($mq-md) {
      bottom: var(--hero-pos-md, var(--hero-pos-sm, var(--fallback-xs-pos)));
    }
    @include mq($mq-lg) {
      bottom: var(--hero-pos-lg, var(--hero-pos-md, var(--hero-pos-sm, var(--fallback-xs-pos))));
    }
    @include mq($mq-xl) {
      bottom: var(
        --hero-pos-xl,
        var(--hero-pos-lg, var(--hero-pos-md, var(--hero-pos-sm, var(--fallback-xs-pos))))
      );
    }
  }
  @include mq(false, $mq-sm - 1) {
    :deep(.product-offer-tile-xl__content-middle) {
      flex: 0 0 40%;
    }

    :deep(.product-offer-tile-xl__content-bottom) {
      flex: 0 0 60%;
    }

    :deep(.product-offer-tile-xl__button) {
      display: flex;
      justify-content: flex-end;
      margin-right: 0;
      padding-left: 0;
    }
  }
  :deep(.product-offer-color-select__item) {
    padding-bottom: 6px;
  }
  :deep(.product-offer-color-select__list) {
    flex-wrap: wrap;
    max-width: 100%;
  }
  &--video-btn-right {
    :deep(.video-player) {
      --play-button-position-left: calc(100% - 50px);
    }
  }

  &__image {
    :deep(img) {
      width: 100%;
      aspect-ratio: 1 / 1;
    }
  }

  &__logo {
    height: 25px;
  }
}
</style>
